import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearActionFeedback,
  fetchRemediableChecks,
} from "../../../../store/remediation/remediationSlice";

/** True when the current user is in the Cognito `Administrator` group. */
export const useIsRemediationAdmin = () => {
  const profile = useSelector((s) => s.profile);
  return useMemo(
    () => Array.isArray(profile?.userRoles) && profile.userRoles.includes("Administrator"),
    [profile]
  );
};

/**
 * Loads remediable check IDs once per session (initial `idle` only).
 *
 * Do NOT retry on `failed` — a CORS / network error would otherwise loop:
 * failed → effect runs → dispatch → loading → failed → …
 *
 * Mount this once per page (Remediation Hub shell, CSPM grid), not per table row.
 */
export const useFetchRemediableChecksOnce = () => {
  const dispatch = useDispatch();
  const status = useSelector((s) => s.remediation?.remediableChecks?.status);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchRemediableChecks());
    }
  }, [dispatch, status]);
};

/** Debounce any value (used for the list-search box). */
export const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

/**
 * Returns true when the document tab is visible. Used to back off polling.
 */
export const usePageVisible = () => {
  const [visible, setVisible] = useState(
    typeof document !== "undefined" ? !document.hidden : true
  );
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
};

/**
 * Runs `fn` every `seconds` seconds. When the tab is backgrounded, the
 * interval is multiplied by 4 (per integration guide). When the tab comes
 * back to focus we fire immediately and resume on the live cadence.
 */
export const usePollingInterval = (fn, seconds, enabled = true) => {
  const cb = useRef(fn);
  const visible = usePageVisible();
  useEffect(() => {
    cb.current = fn;
  }, [fn]);

  // Fire on visibility regain.
  useEffect(() => {
    if (visible && enabled) cb.current();
  }, [visible, enabled]);

  useEffect(() => {
    if (!enabled || !seconds) return undefined;
    const effective = visible ? seconds : seconds * 4;
    const handle = setInterval(() => cb.current(), Math.max(1, effective) * 1000);
    return () => clearInterval(handle);
  }, [seconds, visible, enabled]);
};

/** All remediation timestamps from the API are UTC — label them clearly in the UI. */
export const formatAbsolute = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const utc = d.toLocaleString("en-US", {
      timeZone: "UTC",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${utc} UTC`;
  } catch (_e) {
    return String(iso);
  }
};

/** Cheap relative-time formatter (based on the user's local clock vs UTC instant). */
export const formatRelative = (iso) => {
  if (!iso) return "—";
  const ms = Date.parse(iso) - Date.now();
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / 86_400_000);
  const past = ms < 0;
  const t = past ? "ago" : "from now";
  if (mins < 1) return past ? "just now" : "in <1m";
  if (mins < 60) return past ? `${mins}m ${t}` : `in ${mins}m`;
  if (hours < 24) return past ? `${hours}h ${t}` : `in ${hours}h`;
  return past ? `${days}d ${t}` : `in ${days}d`;
};

/**
 * Watch the slice's `actionFeedback` envelope for completions of a given kind
 * and run a callback exactly once per emission. The hook owns the
 * `clearActionFeedback` dispatch so callers don't have to.
 *
 * Usage:
 *   useActionFeedback("approve", ({ succeeded, skipped, error }) => {
 *     if (error) showSnackbar({ message: friendlyErrorMessage(error), severity: "error" });
 *     else showSnackbar({ message: `Approved ${succeeded.length}.`, severity: "success" });
 *     reloadCurrent();
 *   });
 *
 * `kinds` may be a string or an array of strings.
 */
export const useActionFeedback = (kinds, handler) => {
  const dispatch = useDispatch();
  const feedback = useSelector((s) => s.remediation?.actionFeedback);
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  useEffect(() => {
    if (!feedback) return;
    const set = Array.isArray(kinds) ? kinds : [kinds];
    if (!set.includes(feedback.kind)) return;
    try {
      handlerRef.current?.(feedback);
    } finally {
      dispatch(clearActionFeedback());
    }
  }, [feedback, kinds, dispatch]);
};

